import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useMutation, useSuspenseQuery } from '@apollo/client/react'
import { AdminAPI, getFragment, GraphQL, SharedAPI } from '@guestgoodie/graphql'
import { useLocation } from 'wouter'

export function useLayout(version?: number) {
  const { data } = useSuspenseQuery(AdminAPI.layout.GET_ALL)

  return useMemo(() => {
    return data.getLayouts
      .map(frag => getFragment(SharedAPI.layout.LAYOUT_FRAGMENT, frag))
      .find(l => l.version === version)
  }, [data, version])
}

export default function useLayouts() {
  const [, navigate] = useLocation()
  const { data } = useSuspenseQuery(AdminAPI.layout.GET_ALL)

  const [createLayout, { loading: createLoading }] = useMutation(
    AdminAPI.layout.CREATE_DRAFT
  )
  const [publishLayout, { loading: publishLoading }] = useMutation(
    AdminAPI.layout.PUBLISH
  )
  const [removeLayout, { loading: removeLoading }] = useMutation(
    AdminAPI.layout.DELETE
  )

  const navigateAfterReload = useRef<number | null>(null)

  const layouts = useMemo(() => {
    return data.getLayouts
      .map(frag => getFragment(SharedAPI.layout.LAYOUT_FRAGMENT, frag))
      .toSorted((layoutA, layoutB) => layoutB.version - layoutA.version)
  }, [data])

  const changeLayout = useCallback(
    (version: number) => {
      navigate(`/layout/${version}`)
    },
    [navigate]
  )

  useEffect(() => {
    if (!navigateAfterReload.current) return

    changeLayout(navigateAfterReload.current)
    navigateAfterReload.current = null
  }, [layouts, changeLayout])

  const create = useCallback(
    (id: number) => {
      createLayout({
        variables: { id },
        refetchQueries: [AdminAPI.layout.GET_ALL]
      }).then(({ data }) => {
        const layout = getFragment(
          SharedAPI.layout.LAYOUT_FRAGMENT,
          data?.createLayoutDraft
        )

        if (!layout) return

        navigateAfterReload.current = layout?.version
      })
    },
    [createLayout]
  )

  const publish = useCallback(
    (id: number) => {
      const layout = layouts.find(l => l.id === id)

      if (!layout || layout.state === GraphQL.LayoutState.Published) return

      publishLayout({
        variables: { id },
        refetchQueries: [AdminAPI.layout.GET_ALL]
      })
    },
    [layouts, publishLayout]
  )

  const remove = useCallback(
    (id: number) => {
      const layout = layouts.find(l => l.id === id)

      if (!layout || layout.state === GraphQL.LayoutState.Published) return

      removeLayout({
        variables: { id },
        refetchQueries: [AdminAPI.layout.GET_ALL]
      })
    },
    [layouts, removeLayout]
  )

  return {
    create,
    remove,
    publish,
    changeLayout,
    layouts,
    createLoading,
    publishLoading,
    removeLoading
  }
}
